// react
import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// components
import { Dashboard as DashboardPage } from '@karehero/llama';

// hooks
import { useActionPlan } from 'hooks';

// store
import { useDispatch, useSelector } from 'react-redux';
import { setCareCircleId } from 'store/slices/careCircleSlice';
import {
  useBookmarkArticleMutation,
  useGetAllArticleBookmarkQuery,
  useGetAllSuggestedArticleQuery,
} from 'store/api/cms';
import { getAccount } from 'store/slices/iamSlice';
import { usePushNotification } from 'hooks/usePushNotification';
import useIntercom from 'hooks/useIntercom';

// launchdarkly
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

/**
 * Dashboard is the dashboard page.
 */
const Dashboard = () => {
  // hooks
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const {
    actionPlanItemsTodo,
    allActionPlanItems,
    markActionDismissed,
    actionPlanCategories,
  } = useActionPlan();
  const { data: articleBookmarks } = useGetAllArticleBookmarkQuery();
  const { data: suggestedArticles, isLoading: isArticlesLoading } =
    useGetAllSuggestedArticleQuery();
  const [bookmarkArticle] = useBookmarkArticleMutation();
  const account = useSelector(getAccount);
  const { registerForPushNotifications, cleanup } = usePushNotification();
  const flags = useFlags();
  const navigate = useNavigate();
  const { showIntercom } = useIntercom();

  // memos
  const computedSuggestedArticles = useMemo(
    () => ({
      articles: suggestedArticles,
      bookmarkedArticles: articleBookmarks,
      onBookmark: (article: any) => bookmarkArticle(article._id),
      isLoading: isArticlesLoading,
    }),
    [articleBookmarks, suggestedArticles, bookmarkArticle, isArticlesLoading],
  );

  // effects
  useEffect(() => {
    const careCircleId = searchParams.get('careCircleId');
    if (!careCircleId) {
      return;
    }

    // check if valid uuid
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(careCircleId)) {
      return;
    }

    dispatch(setCareCircleId(careCircleId));
  }, [dispatch, searchParams]);

  // call registration when component mounts
  useEffect(() => {
    if (flags.nativePushNotification) {
      registerForPushNotifications();
      return cleanup;
    }
  }, [registerForPushNotifications, cleanup, flags]);

  // native push notifications deep links handler
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !flags.nativePushNotification) return;

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (actionPerformed: any) => {
        const notification = actionPerformed.notification;

        const deepLink = notification.data['deep-link'];
        if (deepLink) {
          try {
            const url = new URL(deepLink);
            const path = url.pathname;
            navigate(path);
          } catch (error) {
            console.error('Invalid deep link URL:', error);
          }
        }
      },
    );

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [navigate, flags]);

  return (
    <DashboardPage
      suggestedArticles={computedSuggestedArticles}
      actionPlanItems={actionPlanItemsTodo}
      allActionPlanItems={allActionPlanItems}
      onActionPlanItemDismiss={markActionDismissed}
      account={account}
      actionPlanCategories={actionPlanCategories}
      showIntercom={showIntercom}
    />
  );
};

export default Dashboard;
